(function () {
  const TRANSITION_MS = 650;
  // Ignore tiny mouse jitter so product/collection clicks still work
  const DRAG_THRESHOLD = 8;
  const instances = new WeakMap();

  function ensureDragStyles() {
    if (document.getElementById('top-infinite-carousel-drag-style')) return;
    const style = document.createElement('style');
    style.id = 'top-infinite-carousel-drag-style';
    style.textContent = [
      '.top-infinite-carousel__track{cursor:grab;-webkit-user-select:none;user-select:none;}',
      /* Kill snap + smooth scroll while dragging so long drags move freely */
      '.top-infinite-carousel__track.is-dragging{cursor:grabbing!important;scroll-snap-type:none!important;scroll-behavior:auto!important;}',
      '.top-infinite-carousel__track.is-dragging > *{scroll-snap-align:none!important;pointer-events:none!important;-webkit-user-drag:none!important;}'
    ].join('');
    document.head.appendChild(style);
  }

  function initInfiniteCarousel(root) {
    if (!root) return null;

    if (instances.has(root)) {
      instances.get(root)();
    }

    if (root.dataset.carouselEnabled === 'false') return null;

    const track = root.querySelector('[data-carousel-track]');
    const prev = root.querySelector('[data-carousel-prev]');
    const next = root.querySelector('[data-carousel-next]');

    if (!track || !prev || !next) return null;

    ensureDragStyles();

    let autoplayTimer = null;
    let resizeTimer = null;
    let isAdjustingScroll = false;
    let isStepping = false;
    let isPointerDown = false;
    let isDragging = false;
    let hasDragged = false;
    let dragLastX = 0;
    let dragDistance = 0;
    let activePointerId = null;

    const autoplay = root.dataset.carouselAutoplay === 'true';
    const infinite = root.dataset.carouselInfinite !== 'false';
    const speed = (parseInt(root.dataset.carouselSpeed, 10) || 4) * 1000;
    const pauseOnHover = root.dataset.carouselPauseHover !== 'false';
    const itemSelector = root.dataset.carouselItem || '.top-infinite-carousel__item';

    const removeClones = () => {
      track.querySelectorAll('[data-carousel-clone]').forEach((node) => node.remove());
      track.dataset.singleSetWidth = '';
    };

    const appendCloneSet = (originals) => {
      originals.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('data-carousel-clone', 'true');
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('a').forEach((link) => {
          link.setAttribute('tabindex', '-1');
          link.setAttribute('aria-hidden', 'true');
        });
        // Quick view modals/buttons must not be duplicated in carousel clones
        clone.querySelectorAll('.modal--quick-shop').forEach((node) => node.remove());
        clone.querySelectorAll('.quick-product__btn').forEach((node) => node.remove());
        clone.querySelectorAll('.grid-product[data-quick-shop-preloaded]').forEach((node) => {
          node.removeAttribute('data-quick-shop-preloaded');
        });
        clone.querySelectorAll('img').forEach((img) => {
          img.loading = 'lazy';
          img.fetchPriority = 'low';
          img.removeAttribute('fetchpriority');
        });
        track.appendChild(clone);
      });
    };

    const setupClones = () => {
      removeClones();
      if (!infinite) return 0;

      const originals = [...track.children].filter(
        (node) => node.matches(itemSelector) && !node.hasAttribute('data-carousel-clone')
      );

      if (originals.length < 1) return 0;

      const singleSetWidth = track.scrollWidth;
      if (singleSetWidth < 1) return 0;

      appendCloneSet(originals);

      // One extra set is enough for looping; extra copies cost layout/JS time
      let safety = 0;
      while (track.scrollWidth <= track.clientWidth && safety < 2) {
        appendCloneSet(originals);
        safety += 1;
      }

      if (track.scrollWidth <= track.clientWidth) return 0;

      track.dataset.singleSetWidth = String(singleSetWidth);
      return singleSetWidth;
    };

    const getScrollMetrics = () => {
      const item = track.querySelector(`${itemSelector}:not([data-carousel-clone])`);
      const gap = parseFloat(getComputedStyle(track).gap) || 0;
      const itemWidth = item ? item.offsetWidth + gap : 0;
      const maxScroll = track.scrollWidth - track.clientWidth;
      const singleSetWidth = parseFloat(track.dataset.singleSetWidth) || maxScroll;
      return { itemWidth, maxScroll, singleSetWidth };
    };

    const normalizeInfiniteScroll = () => {
      if (!infinite || isAdjustingScroll) return;

      const singleSetWidth = parseFloat(track.dataset.singleSetWidth);
      if (!singleSetWidth) return;

      let left = track.scrollLeft;
      let changed = false;

      while (left >= singleSetWidth - 0.5) {
        left -= singleSetWidth;
        changed = true;
      }
      while (left < 0) {
        left += singleSetWidth;
        changed = true;
      }

      if (!changed) return;

      isAdjustingScroll = true;
      track.classList.add('top-infinite-carousel__track--instant');
      track.scrollLeft = left;
      track.classList.remove('top-infinite-carousel__track--instant');
      isAdjustingScroll = false;
    };

    const updateNav = () => {
      const { maxScroll } = getScrollMetrics();

      if (maxScroll <= 1) {
        prev.disabled = true;
        next.disabled = true;
        return;
      }

      if (infinite && track.dataset.singleSetWidth) {
        prev.disabled = false;
        next.disabled = false;
        return;
      }

      prev.disabled = track.scrollLeft <= 1;
      next.disabled = track.scrollLeft >= maxScroll - 1;
    };

    const afterTransition = (callback) => {
      if ('onscrollend' in window) {
        const onEnd = () => {
          track.removeEventListener('scrollend', onEnd);
          callback();
        };
        track.addEventListener('scrollend', onEnd, { once: true });
        return;
      }

      setTimeout(callback, TRANSITION_MS);
    };

    const getStepAmount = () => {
      const { itemWidth, maxScroll } = getScrollMetrics();
      if (!itemWidth) return 0;

      if (infinite && track.dataset.singleSetWidth) return itemWidth;

      if (maxScroll <= 1) return 0;

      const visibleCount = Math.max(1, Math.floor(track.clientWidth / itemWidth));
      return itemWidth * visibleCount;
    };

    const scrollCarousel = (direction, onComplete) => {
      if (isStepping || isDragging) return;

      const amount = getStepAmount();
      if (!amount) return;

      const { maxScroll } = getScrollMetrics();

      if (!infinite || !track.dataset.singleSetWidth) {
        if (direction > 0 && track.scrollLeft >= maxScroll - 1) {
          if (onComplete) onComplete();
          return;
        }
        if (direction < 0 && track.scrollLeft <= 1) {
          if (onComplete) onComplete();
          return;
        }
      }

      isStepping = true;
      track.classList.add('top-infinite-carousel__track--animating');

      const target = track.scrollLeft + direction * amount;
      track.scrollTo({ left: target, behavior: 'smooth' });

      afterTransition(() => {
        normalizeInfiniteScroll();
        updateNav();
        track.classList.remove('top-infinite-carousel__track--animating');
        isStepping = false;
        if (onComplete) onComplete();
      });
    };

    const stopAutoplay = () => {
      if (autoplayTimer) {
        clearTimeout(autoplayTimer);
        autoplayTimer = null;
      }
    };

    const scheduleAutoplay = (delay) => {
      stopAutoplay();
      if (!autoplay || (pauseOnHover && root.matches(':hover'))) return;

      autoplayTimer = setTimeout(() => {
        scrollCarousel(1, () => {
          const pause = Math.max(300, speed - TRANSITION_MS);
          scheduleAutoplay(pause);
        });
      }, delay);
    };

    const startAutoplay = () => {
      stopAutoplay();
      if (!autoplay || isDragging || isPointerDown) return;
      scheduleAutoplay(speed);
    };

    const rebuildCarousel = () => {
      const previousOffset = track.scrollLeft;
      setupClones();
      if (track.dataset.singleSetWidth) {
        const singleSetWidth = parseFloat(track.dataset.singleSetWidth);
        track.scrollLeft = previousOffset % singleSetWidth;
      }
      updateNav();
    };

    const onPrevClick = () => {
      stopAutoplay();
      scrollCarousel(-1, () => startAutoplay());
    };

    const onNextClick = () => {
      stopAutoplay();
      scrollCarousel(1, () => startAutoplay());
    };

    const onScroll = () => {
      if (!isStepping) normalizeInfiniteScroll();
      updateNav();
    };

    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(rebuildCarousel, 150);
    };

    const onMouseEnter = () => {
      if (pauseOnHover) stopAutoplay();
    };

    const onMouseLeave = () => startAutoplay();

    const endDrag = (event) => {
      if (!isPointerDown) return;
      if (activePointerId != null && event && event.pointerId != null && event.pointerId !== activePointerId) {
        return;
      }

      const wasDragging = isDragging;
      isPointerDown = false;
      isDragging = false;
      activePointerId = null;
      track.classList.remove('is-dragging');

      if (wasDragging) {
        normalizeInfiniteScroll();
        updateNav();
      }

      startAutoplay();
    };

    const onPointerDown = (event) => {
      // Keep native touch scrolling; enable mouse-drag on desktop
      if (event.pointerType && event.pointerType !== 'mouse') return;
      if (event.button != null && event.button !== 0) return;
      if (isStepping) return;

      // Pending only — do not steal clicks until movement passes threshold
      isPointerDown = true;
      isDragging = false;
      hasDragged = false;
      activePointerId = event.pointerId;
      dragLastX = event.clientX;
      dragDistance = 0;
    };

    const onPointerMove = (event) => {
      if (!isPointerDown) return;
      if (activePointerId != null && event.pointerId != null && event.pointerId !== activePointerId) {
        return;
      }

      const dx = event.clientX - dragLastX;
      dragLastX = event.clientX;
      dragDistance += Math.abs(dx);

      if (!isDragging) {
        if (dragDistance <= DRAG_THRESHOLD) return;

        isDragging = true;
        hasDragged = true;
        track.classList.add('is-dragging');
        track.classList.remove('top-infinite-carousel__track--animating');
        stopAutoplay();

        if (track.setPointerCapture && activePointerId != null) {
          try {
            track.setPointerCapture(activePointerId);
          } catch (e) {}
        }
      }

      if (!dx) return;
      if (event.cancelable) event.preventDefault();

      track.scrollLeft -= dx;
      normalizeInfiniteScroll();
      updateNav();
    };

    const onPointerUp = (event) => endDrag(event);
    const onPointerCancel = (event) => endDrag(event);

    const onClickCapture = (event) => {
      if (!hasDragged) return;
      event.preventDefault();
      event.stopPropagation();
      hasDragged = false;
    };

    const onDragStart = (event) => {
      // Block native image drag only while carousel-dragging
      if (!isDragging && !hasDragged) return;
      event.preventDefault();
    };

    rebuildCarousel();

    prev.addEventListener('click', onPrevClick);
    next.addEventListener('click', onNextClick);
    track.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    track.addEventListener('pointerdown', onPointerDown);
    track.addEventListener('pointermove', onPointerMove);
    track.addEventListener('pointerup', onPointerUp);
    track.addEventListener('pointercancel', onPointerCancel);
    track.addEventListener('lostpointercapture', onPointerUp);
    track.addEventListener('click', onClickCapture, true);
    track.addEventListener('dragstart', onDragStart);

    if (pauseOnHover) {
      root.addEventListener('mouseenter', onMouseEnter);
      root.addEventListener('mouseleave', onMouseLeave);
    }

    startAutoplay();

    const destroy = () => {
      stopAutoplay();
      clearTimeout(resizeTimer);
      isStepping = false;
      isPointerDown = false;
      isDragging = false;
      hasDragged = false;
      activePointerId = null;
      removeClones();
      track.classList.remove(
        'top-infinite-carousel__track--animating',
        'top-infinite-carousel__track--instant',
        'is-dragging'
      );
      prev.removeEventListener('click', onPrevClick);
      next.removeEventListener('click', onNextClick);
      track.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      track.removeEventListener('pointerdown', onPointerDown);
      track.removeEventListener('pointermove', onPointerMove);
      track.removeEventListener('pointerup', onPointerUp);
      track.removeEventListener('pointercancel', onPointerCancel);
      track.removeEventListener('lostpointercapture', onPointerUp);
      track.removeEventListener('click', onClickCapture, true);
      track.removeEventListener('dragstart', onDragStart);
      if (pauseOnHover) {
        root.removeEventListener('mouseenter', onMouseEnter);
        root.removeEventListener('mouseleave', onMouseLeave);
      }
      instances.delete(root);
    };

    instances.set(root, destroy);
    return destroy;
  }

  const pending = new WeakMap();

  function scheduleInit(root) {
    if (!root || instances.has(root) || pending.has(root)) return;

    if (window.Shopify && Shopify.designMode) {
      initInfiniteCarousel(root);
      return;
    }

    const run = function () {
      pending.delete(root);
      if (!root.isConnected || instances.has(root)) return;
      initInfiniteCarousel(root);
    };

    if (!('IntersectionObserver' in window)) {
      run();
      return;
    }

    const io = new IntersectionObserver(function (entries) {
      for (let i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          io.disconnect();
          run();
          break;
        }
      }
    }, { rootMargin: '120px 0px' });

    pending.set(root, function () {
      io.disconnect();
    });
    io.observe(root);
  }

  function destroyCarousel(root) {
    if (!root) return;
    const cancel = pending.get(root);
    if (cancel) {
      cancel();
      pending.delete(root);
    }
    if (instances.has(root)) {
      instances.get(root)();
    }
  }

  window.TopInfiniteCarousel = {
    init: scheduleInit,
    destroy: destroyCarousel
  };
})();
